import { Divider } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UseMutationResult, useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { ViewApI } from 'api/views';
import {
  ViewCustomisationPayload,
  ViewCustomisationUpdate,
} from 'api/views/types';
import { Modal } from 'components/atoms/modal';
import { useViewContext } from '../context';
import { CustomisationTree } from './tree';
import { CustomisationState, initialCustomisationState } from './types';

interface CustomisationModalProps {
  customisationState: CustomisationState;
  setCustomisationState: (state: CustomisationState) => void;
}

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    padding: var(--spacing-05);
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0;
  margin-top: var(--spacing-05);
  width: 100%;
`;

const useViewCustomiseMutation = (): UseMutationResult<
  void,
  unknown,
  ViewCustomisationPayload
> => {
  const queryClient = useQueryClient();
  const viewContext = useViewContext();

  return useMutation<void, unknown, ViewCustomisationPayload>(
    (payload: ViewCustomisationPayload) => ViewApI.customiseView(payload),
    {
      onSuccess: () => queryClient.invalidateQueries(viewContext.queryKey),
    },
  );
};

export const CustomisationModal = ({
  customisationState,
  setCustomisationState,
}: CustomisationModalProps) => {
  const [updates, setUpdates] = useState<ViewCustomisationUpdate[]>([]);
  const { viewType, viewSourceId } = useViewContext();
  const { mutate: customise, isLoading: loading } = useViewCustomiseMutation();
  const { shared, views, visible } = customisationState;

  const doUpdate = (update: ViewCustomisationUpdate) =>
    setUpdates((prev) => [...prev, update]);

  if (!visible) {
    return null;
  }

  return (
    <StyledModal
      visible
      onOk={() => {
        customise(
          { viewType, viewSourceId: viewSourceId ?? null, shared, updates },
          {
            onSuccess: () => {
              setCustomisationState(initialCustomisationState);
              setUpdates([]);
            },
          },
        );
      }}
      onCancel={() => setCustomisationState(initialCustomisationState)}
      destroyOnClose
      maskClosable={false}
      okButtonProps={{ loading }}
      okText={
        <FormattedMessage
          id={`views.customise.modal.${shared ? 'shared' : 'saved'}_cta`}
        />
      }
      title={
        <FormattedMessage
          id={`views.customise.modal.${shared ? 'shared' : 'saved'}_title`}
        />
      }
    >
      <CustomisationTree views={views} doUpdate={doUpdate} />
      <StyledDivider />
    </StyledModal>
  );
};
